import Modal from "../../../components/common/form/Modal";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import InputField from "../../../components/common/form/InputField";

import { skipToken } from "@reduxjs/toolkit/query";
import TextAreaField from "./TextAreaField";
import { createOptionListForSelectTag, postalPattern, postalTitle } from "../../../helpers/utils/Utils";
import NumberFormatField from "./NumberFormatField";
import SelectField from "./SelectField";
import { useGetAllStatesQuery } from "../../../features/data/states/statesApi";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { useGetContactByIdQuery } from "../../../features/hoa/managementCompany/managementCompany";
import ButtonTray from "./ButtonTray";
import { getEnumDisplayNameByConstant } from "../../../helpers/utils/EnumUtils";
import Form from "./Form";


interface props {
    formType: string;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    contactId?: any,
    isReadOnly?: boolean;
    onSubmit: any;
    isLoading?: boolean;
    setContactData?: any
    contactData?: any;
    overrideDismissMethod: any;
    isBranchDetail?: any
}

const AddEditContactForm: FC<props> = ({
    formType,
    show,
    setShow,
    contactId,
    isReadOnly,
    onSubmit,
    isLoading = false,
    setContactData,
    contactData,
    overrideDismissMethod,
    isBranchDetail = false,
}) => {

    const getContactByIdApi = useGetContactByIdQuery(contactId ?? skipToken);
    const { data: stateData } = useGetAllStatesQuery("");
    const { data: enums } = useGetEnumsQuery("");

    useEffect(() => {
        const obj = getContactByIdApi?.data;
        if (obj) {
            const flatData = {
                ...obj,
                address: {
                    ...obj.address,
                    state: obj?.address?.state
                        ? {
                            label: stateData?.find(
                                (el: any) => el.id === obj?.address?.state
                            )?.stateName,
                            value: obj?.address?.state,
                        }
                        : null,
                },
                preferredContactMethod: obj.preferredContactMethod
                    ? {
                        value: obj.preferredContactMethod,
                        label: getEnumDisplayNameByConstant(
                            enums?.["ContactMethod"],
                            obj.preferredContactMethod
                        ),
                    }
                    : null,
            };
            setContactData(flatData);
        }
    }, [getContactByIdApi?.data, stateData, enums]);


    const handleOnChange = (
        e:
            | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | { name: string; value: any }
    ) => {
        const { name, value } = "target" in e ? e.target : e;

        const keys = name.split(".");

        if (keys.length === 2) {
            const [parentKey, childKey] = keys;

            setContactData((prev: any) => ({
                ...prev,
                [parentKey]: {
                    ...prev[parentKey],
                    [childKey]: value,
                },
            }));
        } else {
            setContactData((prev: any) => ({
                ...prev,
                [name]: value,
            }));
        }
    };
    const handleToggle = (property: string) => () => {
        setContactData((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
    };

    return (
        <>
            <Modal
                title={formType + " Contact Form"}
                show={show}
                setShow={setShow}
                className="w-2/5  bg-text-0 rounded-lg shadow-lg text-sm"
                overrideDismissMethod={overrideDismissMethod}
                fields={
                    // @ts-ignore
                    <Form
                        onSubmit={async (e: any) => {
                            e.preventDefault()
                            await onSubmit(contactData)
                            setShow(false)
                        }}
                        fields={
                            <div className="modal-body">

                                <InputField
                                    label="Name"
                                    name="name"
                                    value={contactData?.name || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    required={true}
                                    className={'font-normal'}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />
                                <InputField
                                    label="Mailing Address"
                                    name="address.mailingAddress"
                                    value={contactData?.address?.mailingAddress || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className={'font-normal'}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />
                                <NumberFormatField
                                    placeholder="Enter Phone..."
                                    name="phone"
                                    value={contactData?.phone || ""}
                                    onChange={handleOnChange}
                                    label={"Phone"}
                                    className={'font-normal'}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}

                                />
                                <InputField
                                    label="Email"
                                    name="email"
                                    value={contactData?.email || ""}
                                    type="email"
                                    onChange={handleOnChange}
                                    className={'font-normal mb-2'}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />
                                <InputField
                                    label="Website"
                                    name="website"
                                    value={contactData?.website || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className={'font-normal mb-2'}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />
                                <NumberFormatField
                                    placeholder="Enter Fax..."
                                    value={contactData?.fax || ""}
                                    onChange={handleOnChange}
                                    name="fax"
                                    label={"Fax"}
                                    className={'font-normal'}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />

                                <InputField
                                    label="Zip Code"
                                    name="address.zipCode"
                                    value={contactData?.address?.zipCode || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className={'font-normal mb-2'}
                                    labelClassName={'font-normal'}
                                    pattern={postalPattern} title={postalTitle}
                                    disabled={isReadOnly}
                                />

                                <SelectField
                                    placeholder="Select State..."
                                    value={contactData?.address?.state || null}
                                    options={createOptionListForSelectTag(stateData, "stateName", "stateAbbreviation")}
                                    onChange={(selectedOption) => handleOnChange({
                                        name: "address.state",
                                        value: selectedOption
                                    })}
                                    label="State"
                                    className={'relative col-span-9 font-normal'}
                                    labelClassName={'font-normal'}
                                    isDisabled={isReadOnly}
                                />
                                <InputField
                                    label="City"
                                    name="address.city"
                                    value={contactData?.address?.city || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className="mb-2 font-normal"
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Contact Method...'
                                    value={contactData?.preferredContactMethod || null}
                                    options={createOptionListForSelectTag(enums?.["ContactMethod"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "preferredContactMethod", value: selectedOption })
                                    }}

                                    label={"Contact Method"}
                                    className={'relative col-span-9 font-normal'}
                                    labelClassName={'font-normal'}
                                    isDisabled={isReadOnly}
                                />

                                <TextAreaField
                                    label="Note"
                                    name="note"
                                    value={contactData?.note || ""}
                                    className="mb-2 text-surface-2 font-normal"
                                    onChange={handleOnChange}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />


                                <InputField
                                    name="isPrimary"
                                    label="Is Primary"
                                    checked={contactData?.isPrimary || ""}
                                    type="checkbox"
                                    onChange={handleToggle("isPrimary")}
                                    className="font-normal"
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />

                                {isBranchDetail &&
                                    <InputField
                                        name="shouldNotify"
                                        label="Enable Email Notifications"
                                        checked={contactData?.shouldNotify || ""}
                                        type="checkbox"
                                        onChange={handleToggle("shouldNotify")}
                                        className="font-normal"
                                        labelClassName={'font-normal'}
                                        disabled={isReadOnly}
                                    />

                                }

                                {/* Footer Buttons */}
                                <div className="mt-4 border-t border-t-surface-5 flex justify-end">
                                    <ButtonTray
                                        buttons={[
                                            {
                                                buttonProps: {
                                                    btnText: "Cancel",
                                                    onClick: () => {
                                                        if (overrideDismissMethod) {
                                                            overrideDismissMethod();
                                                        }
                                                        setShow(false)
                                                    },
                                                    type: "close",
                                                },
                                                buttonType: "button"
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Submit",

                                                    type: "submit",
                                                    isLoading: isLoading,
                                                    isDisabled: isReadOnly
                                                },
                                                buttonType: "button",

                                            }
                                        ]}
                                        align={"end"}
                                        gap={2}
                                    />

                                </div>
                            </div>}>

                    </Form>
                }
            />

        </>
    )
}
export default AddEditContactForm;